import { ApkgExtractionError } from '../apkg-import/extract';
import { UploadError } from '../upload/transfer/errors';

/**
 * Searchable context for an error report, read from the error itself. Both
 * clients merge it into every Sentry event in `beforeSend`, so the same error
 * carries the same tags on web and mobile.
 *
 * Tags are short, low-cardinality values to filter and group by; `extra` holds
 * what a reader needs to reproduce one report.
 */
export type ErrorDiagnostics = {
  tags: Record<string, string>;
  extra: Record<string, unknown>;
};

const BODY_LIMIT = 500;

/**
 * The extractor's failure, by what it says. "Failed to open Anki collection
 * database: JsonError { decoding decks }" is FLASHKA-VITE-1ZS; the rest are
 * grouped so a new kind of failure stands out.
 */
export function apkgFailureReason(body: string): string {
  if (/File is not a zip file/i.test(body)) return 'not-a-zip';
  if (/decoding decks/i.test(body)) return 'anki-decks-json';
  if (/Failed to open Anki collection/i.test(body)) return 'anki-collection-open';
  if (/timed? ?out|timeout/i.test(body)) return 'timeout';
  return 'other';
}

export function errorDiagnostics(error: unknown): ErrorDiagnostics | null {
  if (error instanceof UploadError) {
    const tags: Record<string, string> = { 'upload.kind': error.kind };
    if (error.status !== undefined) tags['upload.status'] = String(error.status);
    if (error.where) tags['upload.where'] = error.where;
    if (error.diagnostics) {
      tags['upload.size'] = error.diagnostics.sizeBucket;
      tags['upload.type'] = error.diagnostics.contentType.slice(0, 64);
      tags['upload.retries'] = String(error.diagnostics.retries);
    }
    return { tags, extra: {} };
  }
  if (error instanceof ApkgExtractionError) {
    const tags: Record<string, string> = {
      'apkg.status': String(error.status),
      'apkg.endpoint': error.attempt.endpoint,
      'apkg.reason': apkgFailureReason(error.body),
    };
    if (error.attempt.structuredStatus !== undefined) {
      tags['apkg.structured_status'] = String(error.attempt.structuredStatus);
    }
    return {
      tags,
      extra: {
        apkgFilePath: error.attempt.filePath,
        apkgResponse: error.body.slice(0, BODY_LIMIT),
      },
    };
  }
  return null;
}

type DiagnosableEvent = {
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
};

/** Merge {@link errorDiagnostics} into an event; tags the caller set win. */
export function applyErrorDiagnostics<E extends DiagnosableEvent>(
  event: E,
  hint: { originalException?: unknown } | undefined,
): E {
  const diagnostics = errorDiagnostics(hint?.originalException);
  if (!diagnostics) return event;
  event.tags = { ...diagnostics.tags, ...event.tags };
  event.extra = { ...diagnostics.extra, ...event.extra };
  return event;
}
